import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { Button, IconButton, Slider, Typography } from "@mui/material";
import { AddCircleOutlineOutlined, Delete } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import GoldenSwitch from "../goldenSwitch/GoldenSwitch";
import { useDebounce } from "../../helpers";
import { qrCodeService } from "../../services/qrCodeService";
import { useSnackbar } from "notistack";

const PREFIX = "QRCodeLogo";

const classes = {
  root: `${PREFIX}-root`,
  addLogoButton: `${PREFIX}-addLogoButton`,
  logoButtonsContainer: `${PREFIX}-logoButtonsContainer`,
};

const QRCodeLogoContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.logoButtonsContainer}`]: {
      width: "100%",
      marginBottom: "1rem",
    },
    [`& .${classes.addLogoButton}`]: {
      marginRight: "3rem",
    },
  };
});

const QrCodeLogo = ({
  qrCodeParams,
  setQrCodeParams,
  updateQrParams,
  setOpenBackdrop,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [logoSliders, setLogoSliders] = useState({
    logoWidth: qrCodeParams.qrParams.logoWidth,
    logoHeight: qrCodeParams.qrParams.logoHeight,
    logoOpacity: qrCodeParams.qrParams.logoOpacity,
    logoPadding: qrCodeParams.qrParams.logoPadding,
  });
  let debouncedSlider = useDebounce(logoSliders, 500);

  useEffect(() => {
    setQrCodeParams({
      ...qrCodeParams,
      qrParams: { ...qrCodeParams.qrParams, ...logoSliders },
    });
  }, [debouncedSlider]);

  const handlePaddingStyle = () => {
    if (qrCodeParams.qrParams.logoPaddingStyle === "square")
      updateQrParams("qrParams", "circle", "logoPaddingStyle");
    else updateQrParams("qrParams", "square", "logoPaddingStyle");
  };

  return (
    <QRCodeLogoContainer>
      <div className={classes.logoButtonsContainer}>
        <Button
          className={classes.addLogoButton}
          aria-label="add-logo"
          component="label"
          startIcon={<AddCircleOutlineOutlined />}
          variant="contained"
          onChange={(e) => {
            setOpenBackdrop(true);
            qrCodeService
              .uploadQrLogo(e.target.files)
              .then((res) => {
                updateQrParams("qrParams", res.url, "logoUrl");
                enqueueSnackbar(t("toasts.associateBorderlessImageSuccess"), {
                  variant: "success",
                });
                setOpenBackdrop(false);
              })
              .catch((err) => {
                if (err === 403)
                  enqueueSnackbar(t("toasts.unauthorizedError"), {
                    variant: "error",
                  });
                else
                  enqueueSnackbar(t("toasts.associateBorderlessImageError"), {
                    variant: "error",
                  });
                setOpenBackdrop(false);
              });
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <input type="file" accept="image/*" hidden />
          {t("QrManager.addLogo")}
        </Button>
        {qrCodeParams.qrParams.logoUrl && (
          <IconButton
            color="primary"
            size="small"
            onClick={() => {
              URL.revokeObjectURL(qrCodeParams.qrParams.logoUrl);
              updateQrParams("qrParams", "", "logoUrl");
            }}
          >
            <Delete />
          </IconButton>
        )}
      </div>
      {qrCodeParams.qrParams.logoUrl && (
        <>
          <Typography variant="body1">{t("QrManager.logoWidth")}</Typography>
          <Slider
            aria-label="logo-width-slider"
            size="small"
            value={logoSliders.logoWidth}
            onChange={(e) => {
              // updateQrParams("logoWidth", e.target.value, 0)
              setLogoSliders({ ...logoSliders, logoWidth: e.target.value });
            }}
            getAriaValueText={(value) => `${value}°`}
            valueLabelDisplay="auto"
            step={1}
            min={1}
            max={250}
          />
          <Typography variant="body1">{t("QrManager.logoHeight")}</Typography>
          <Slider
            aria-label="logo-height-slider"
            size="small"
            value={logoSliders.logoHeight}
            onChange={(e) => {
              // updateQrParams("logoHeight", e.target.value, 0)
              setLogoSliders({ ...logoSliders, logoHeight: e.target.value });
            }}
            getAriaValueText={(value) => `${value}°`}
            valueLabelDisplay="auto"
            step={1}
            min={1}
            max={250}
          />
          <Typography variant="body1">{t("QrManager.logoOpacity")}</Typography>
          <Slider
            aria-label="logo-opacity-slider"
            size="small"
            value={logoSliders.logoOpacity}
            onChange={(e) => {
              // updateQrParams("logoOpacity", e.target.value, 0)
              setLogoSliders({ ...logoSliders, logoOpacity: e.target.value });
            }}
            getAriaValueText={(value) => `${value}°`}
            valueLabelDisplay="auto"
            step={0.1}
            min={0}
            max={1}
          />
          <Typography variant="body1">{t("QrManager.logoPadding")}</Typography>
          <Slider
            aria-label="logo-padding-slider"
            size="small"
            value={logoSliders.logoPadding}
            onChange={(e) => {
              // updateQrParams("logoPadding", e.target.value, 0)
              setLogoSliders({ ...logoSliders, logoPadding: e.target.value });
            }}
            getAriaValueText={(value) => `${value}°`}
            valueLabelDisplay="auto"
            step={5}
            min={0}
            max={100}
          />
          <GoldenSwitch
            checked={
              qrCodeParams.qrParams.logoPaddingStyle === "square" ? false : true
            }
            onChange={() => handlePaddingStyle()}
            label={t("QrManager.logoPaddingShape")}
          />
        </>
      )}
    </QRCodeLogoContainer>
  );
};

export default QrCodeLogo;
